/**
 * Common Helper Functions
 * Centralized utility functions used across the application
 */

import { toast } from "react-toastify";

// ============================================================================
// NOTIFICATION HELPERS
// ============================================================================

/**
 * Creates notification functions for a component
 * @returns {Object} Object with notify_info, notify_error, and notify_success functions
 */
export const createNotificationHelpers = () => ({
    notify_info: (message) => toast.info(`${message}`),
    notify_error: (message) => toast.error(`${message}`),
    notify_success: (message) => toast.success(`${message}`),
});

// ============================================================================
// ERROR HANDLING HELPERS
// ============================================================================

/**
 * Handles API response errors and shows appropriate notifications
 * @param {Response} response - The fetch API response object
 * @param {Object} results - Parsed JSON response
 * @param {Function} notifyError - Error notification function
 * @param {string} defaultMessage - Default error message
 */
export const handleApiError = (response, results, notifyError, defaultMessage = "Something went wrong") => {
    if (response.status === 401) {
        notifyError(`Unauthorized: ${results.message || "You are not authorized to perform this action"}`);
        window.location.href = "/logout";
    } else if (response.status === 400) {
        notifyError(`Bad Request: ${results.message || "Invalid data"}`);
    } else if (response.status === 409) {
        notifyError(`Conflict: ${results.message || "Data already exists"}`);
    } else if (response.status === 500) {
        notifyError(`Server Error: ${results.message || "Something went wrong"}`);
    } else {
        notifyError(`Unexpected Error: ${response.status} - ${results.message || defaultMessage}`);
    }
};

/**
 * Handles fetch errors (network errors, etc.)
 * @param {Function} notifyError - Error notification function
 * @param {string} message - Custom error message
 */
export const handleFetchError = (notifyError, message = "Failed to load data. Please check your network connection.") => {
    notifyError(message);
};

// ============================================================================
// DATE FORMATTING HELPERS
// ============================================================================

/**
 * Formats ISO date string to readable format (YYYY-MM-DD HH:MM:SS)
 * @param {string} isoString - ISO date string
 * @returns {string} Formatted date string or "N/A" if invalid
 */
export const formatDateTime = (isoString) => {
    if (!isoString) return "N/A";
    const date = new Date(isoString);

    // Check if date is valid
    if (isNaN(date.getTime())) return "N/A";

    const year = date.getFullYear().toString();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * Formats date to short format (MM/DD/YY)
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string or "N/A"
 */
export const formatDateShort = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
    });
};

/**
 * Formats date to short format for sorting (YYYY-MM-DD)
 * @param {string} isoString - ISO date string
 * @returns {string} Formatted date string or 'N/A'
 */
export const formatDateSort = (isoString) => {
    if (!isoString) return "N/A";
    const date = new Date(isoString);
    const year = date.getFullYear().toString();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Formats date to readable format (MMM DD, YYYY)
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string or "N/A"
 */
export const formatDateReadable = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
};

/**
 * Formats date to long format (Month Day, Year)
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string or "N/A"
 */
export const formatDateLong = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};

/**
 * Formats date for period display (YYYY/MM/DD)
 * @param {string} isoString - ISO date string
 * @returns {string} Formatted date string or 'N/A'
 */
export const formatDatePeriod = (isoString) => {
    if (!isoString) return "N/A";
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
};

/**
 * Formats date to YYYY-MM-DD format
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Formatted date string (YYYY-MM-DD)
 */
export const formatDate = (date) => {
    const d = new Date(date);
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
};

// ============================================================================
// CURRENCY & NUMBER FORMATTING HELPERS
// ============================================================================

/**
 * Formats amount to currency string
 * @param {number|string} amount - Amount value
 * @param {string} currency - Currency symbol (default: '$')
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = '$', decimals = 2) => {
    const numAmount = parseFloat(amount || 0);
    return `${currency}${numAmount.toFixed(decimals)}`;
};

/**
 * Formats number with commas for thousands
 * @param {number|string} number - Number to format
 * @returns {string} Formatted number string
 */
export const formatNumber = (number) => {
    return Number(number || 0).toLocaleString('en-US');
};

// ============================================================================
// URL & QUERY PARAMETER HELPERS
// ============================================================================

/**
 * Gets store domain from URL query parameters
 * @param {string} search - URL search string (location.search)
 * @returns {string|null} Store domain or null
 */
export const getStoreDomainFromQuery = (search) => {
    if (!search) return null;
    const params = new URLSearchParams(search);
    return params.get('store');
};

/**
 * Builds URL with query parameters
 * @param {string} pathname - Base path
 * @param {Object} params - Query parameters object
 * @returns {string} URL with query string
 */
export const buildUrlWithParams = (pathname, params = {}) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
            searchParams.append(key, value);
        }
    });
    const queryString = searchParams.toString();
    return queryString ? `${pathname}?${queryString}` : pathname;
};

// ============================================================================
// DATA PROCESSING HELPERS
// ============================================================================

/**
 * Extracts record ID from full record path
 * @param {string} recordId - Full record ID path
 * @returns {string} Last segment of the path
 */
export const extractRecordId = (recordId) => {
    return recordId?.split('/').pop() || 'N/A';
};

/**
 * Safely parses JSON string
 * @param {string} jsonString - JSON string to parse
 * @param {any} defaultValue - Default value if parsing fails
 * @returns {any} Parsed object or default value
 */
export const safeJsonParse = (jsonString, defaultValue = null) => {
    try {
        return JSON.parse(jsonString);
    } catch (error) {
        return defaultValue;
    }
};

/**
 * Safely stringifies object to JSON
 * @param {any} obj - Object to stringify
 * @param {string} defaultValue - Default value if stringify fails
 * @returns {string} JSON string or default value
 */
export const safeJsonStringify = (obj, defaultValue = '{}') => {
    try {
        return JSON.stringify(obj);
    } catch (error) {
        return defaultValue;
    }
};

/**
 * Gets nested object value safely
 * @param {Object} obj - Object to access
 * @param {string} path - Dot-separated path (e.g., 'user.profile.name')
 * @param {any} defaultValue - Default value if path doesn't exist
 * @returns {any} Value at path or default value
 */
export const getNestedValue = (obj, path, defaultValue = null) => {
    if (!obj || !path) return defaultValue;
    const keys = path.split('.');
    let result = obj;
    for (const key of keys) {
        if (result === null || result === undefined) return defaultValue;
        result = result[key];
    }
    return result !== undefined ? result : defaultValue;
};

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Validates email format
 * @param {string} email - Email address to validate
 * @returns {boolean} True if valid email format
 */
export const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

/**
 * Validates URL format
 * @param {string} url - URL to validate
 * @returns {boolean} True if valid URL format
 */
export const isValidUrl = (url) => {
    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
};

/**
 * Checks if value is empty (null, undefined, empty string, empty array, empty object)
 * @param {any} value - Value to check
 * @returns {boolean} True if value is empty
 */
export const isEmpty = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim().length === 0;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
};

// ============================================================================
// ARRAY & OBJECT HELPERS
// ============================================================================

/**
 * Removes duplicates from array
 * @param {Array} array - Array to process
 * @param {string} key - Key to compare (optional, for array of objects)
 * @returns {Array} Array without duplicates
 */
export const removeDuplicates = (array, key = null) => {
    if (!key) {
        return [...new Set(array)];
    }
    const seen = new Set();
    return array.filter(item => {
        const value = item[key];
        if (seen.has(value)) return false;
        seen.add(value);
        return true;
    });
};

/**
 * Groups array items by a key
 * @param {Array} array - Array to group
 * @param {string|Function} key - Key to group by or function to extract key
 * @returns {Object} Grouped object
 */
export const groupBy = (array, key) => {
    return array.reduce((result, item) => {
        const groupKey = typeof key === 'function' ? key(item) : item[key];
        if (!result[groupKey]) {
            result[groupKey] = [];
        }
        result[groupKey].push(item);
        return result;
    }, {});
};

/**
 * Sorts array by key
 * @param {Array} array - Array to sort
 * @param {string} key - Key to sort by
 * @param {boolean} ascending - Sort ascending (default: true)
 * @returns {Array} Sorted array
 */
export const sortBy = (array, key, ascending = true) => {
    return [...array].sort((a, b) => {
        const aVal = a[key];
        const bVal = b[key];
        if (aVal < bVal) return ascending ? -1 : 1;
        if (aVal > bVal) return ascending ? 1 : -1;
        return 0;
    });
};

// ============================================================================
// STRING HELPERS
// ============================================================================

/**
 * Capitalizes first letter of string
 * @param {string} str - String to capitalize
 * @returns {string} Capitalized string
 */
export const capitalize = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Converts string to camelCase
 * @param {string} str - String to convert
 * @returns {string} CamelCase string
 */
export const toCamelCase = (str) => {
    return str.replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
        return index === 0 ? word.toLowerCase() : word.toUpperCase();
    }).replace(/\s+/g, '');
};

/**
 * Truncates string to specified length
 * @param {string} str - String to truncate
 * @param {number} length - Maximum length
 * @param {string} suffix - Suffix to append (default: '...')
 * @returns {string} Truncated string
 */
export const truncate = (str, length, suffix = '...') => {
    if (!str || str.length <= length) return str;
    return str.substring(0, length) + suffix;
};

// ============================================================================
// STORE/MERCHANT HELPERS
// ============================================================================

/**
 * Gets store domain from various sources
 * @param {Object} store_details - Store details object
 * @param {Object} overrides - Override values
 * @returns {string} Store domain
 */
export const getStoreDomain = (store_details, overrides = {}) => {
    return store_details?.store_domain || store_details?.shop || store_details?.store || overrides?.store_domain || '';
};

/**
 * Extracts location data from React Router location object
 * @param {Object} location - React Router location object
 * @returns {Object} Extracted data and storeStatus
 */
export const extractLocationData = (location) => {
    const stateData = location?.state || {};
    const legacyData = location?.data ? location : {};
    return {
        data: stateData.data || legacyData.data || null,
        storeStatus: stateData.storeStatus || legacyData.storeStatus || 'active'
    };
};

/**
 * Gets domain to fetch from location and data
 * @param {Object} location - React Router location object
 * @param {Object} data - Data object
 * @returns {string|null} Store domain or null
 */
export const getDomainToFetch = (location, data) => {
    const params = new URLSearchParams(location?.search || '');
    const storeFromQuery = params.get('store');
    const fallbackDomain = (typeof data === 'object' && data?.store_domain) ? data.store_domain : null;
    return storeFromQuery || fallbackDomain;
};

// ============================================================================
// DEBOUNCE & THROTTLE HELPERS
// ============================================================================

/**
 * Creates a debounced function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

/**
 * Creates a throttled function
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export const throttle = (func, limit) => {
    let inThrottle;
    return function (...args) {
        if (!inThrottle) {
            func.apply(this, args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    };
};

// ============================================================================
// COUPON SCANNER HELPERS
// ============================================================================

/**
 * Extracts domain name from a full domain string
 * Handles www subdomains and multi-level domains
 * @param {string} domain - Full domain string (e.g., "www.example.com", "example.com")
 * @returns {string} Extracted domain name or empty string if invalid
 * @example
 * getDomainName("www.example.com") // returns "example"
 * getDomainName("example.com") // returns "example"
 * getDomainName("sub.example.com") // returns "sub"
 */
export const getDomainName = (domain) => {
    if (!domain) return '';
    const parts = domain.split('.');
    if (parts.length < 2) return '';
    if (parts.length === 3) {
        if (parts[0] === 'www') {
            return parts[1];
        }
        return parts[0];
    }
    return parts[parts.length - 2];
};

/**
 * Processes store handle records to extract domain, handle, and query information
 * Prioritizes custom_params if available, otherwise extracts from domain
 * @param {Object} records - Store handle records object
 * @returns {Object|null} Object with domain, handle, and query properties, or null if no records
 * @example
 * handleInfo({ custom_params: { domain: "example.com", handle: "example", query: "example" } })
 * // returns { domain: "example.com", handle: "example", query: "example" }
 * handleInfo({ domain: "www.example.com" })
 * // returns { domain: "www.example.com", handle: "www", query: "example" }
 */
export const handleStoreInfo = (records) => {
    if (!records) return null;
    const { custom_params, domain } = records;

    // If custom_params exist and have keys, use them
    if (custom_params && Object.keys(custom_params).length > 0) {
        return custom_params;
    }

    // Otherwise, extract from domain
    return {
        domain: domain || '',
        handle: domain?.split('.')?.[0] || '',
        query: getDomainName(domain)
    };
};

/**
 * Parses website URLs string into an array
 * Handles newline-separated and comma-separated URLs
 * @param {string} websiteUrlsString - String containing URLs (newline or comma separated)
 * @returns {Array<string>} Array of trimmed, non-empty URLs
 * @example
 * parseWebsiteUrls("url1.com\nurl2.com,url3.com") // returns ["url1.com", "url2.com", "url3.com"]
 * parseWebsiteUrls("url1.com, url2.com") // returns ["url1.com", "url2.com"]
 */
export const parseWebsiteUrls = (websiteUrlsString) => {
    if (!websiteUrlsString) return [];

    return websiteUrlsString
        .split('\n')
        .map(url => url?.split(',').map(url => url.trim()))
        .flat()
        .filter(url => url !== '');
};

